import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, CircularProgress, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, Delete as DeleteIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import SearchApi from '../../api/platform/searchApi';
import ConfirmationModal from '../../components/Common/ConfirmationModal';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import styles from '../../css/search-save.module.css';
import { ExtendedSearchesModel } from '../../models/platform/searchesModel';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../utils/errorUtils';


interface SavedSearchModalProps {
  isOpen: boolean;
  onRequestClose: (search: ExtendedSearchesModel | null, wasSaved: boolean, wasExecuted: boolean, isNewSearch: boolean) => void;
  onViewEdit: (search: ExtendedSearchesModel) => void;
}

type Order = 'asc' | 'desc';


const SavedSearchesModal: React.FC<SavedSearchModalProps> = ({ isOpen, onRequestClose, onViewEdit }) => {
  const [savedSearches, setSavedSearches] = useState<ExtendedSearchesModel[]>([]);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [isExecuteConfirmationModalOpen, setIsExecuteConfirmationModalOpen] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<ExtendedSearchesModel | null>(null);
  const [searchToExecute, setSearchToExecute] = useState<ExtendedSearchesModel | null>(null);
  const [editSearchId, setEditSearchId] = useState<string | null>(null);
  const [editSearchName, setEditSearchName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof ExtendedSearchesModel>('search_name');
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchSavedSearches = async () => {
      setLoading(true);
      try {
        const response = await SearchApi.getSearchesByAccountId();
        if (response.success) {
          setSavedSearches(response.data);
        } else {
          handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
        }
      } catch (error) {
        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchSavedSearches();
    }
  }, [isOpen]);

  const editNameSave = async (searchId: string, newName: string) => {
    try {
      const response = await SearchApi.updateSearchName(searchId, newName);
      if (response.success) {
        setSavedSearches(savedSearches.map(search =>
          search._id === searchId ? { ...search, search_name: newName } : search
        ));
        setEditSearchId(null);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleRequestSort = (property: keyof ExtendedSearchesModel) => {
    try {
      const isAsc = orderBy === property && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setOrder(newOrder);
      setOrderBy(property);

      const sortedSearches = [...savedSearches].sort((a, b) => {
        const aValue = a[property] ?? '';
        const bValue = b[property] ?? '';

        if (aValue < bValue) {
          return newOrder === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return newOrder === 'asc' ? 1 : -1;
        }
        return 0;
      });

      setSavedSearches(sortedSearches);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleDeleteCancel = () => {
    try {
      setIsDeleteConfirmationModalOpen(false);
      setSearchToDelete(null);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };


  const handleDeleteConfirm = async () => {
    if (searchToDelete && searchToDelete._id) {
      setIsProcessingDelete(true);
      try {
        await SearchApi.deleteSearch(searchToDelete._id);
        setSavedSearches(savedSearches.filter((search) => search._id !== searchToDelete._id));
        setIsDeleteConfirmationModalOpen(false);
        setSearchToDelete(null);
      } catch (error) {
        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      } finally {
        setIsProcessingDelete(false);
      }
    }
  };

  const handleDeleteSearchWithConfirmation = (search: ExtendedSearchesModel) => {
    try {
      setSearchToDelete(search);
      setIsDeleteConfirmationModalOpen(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteCancel = () => {
    try {
      setIsExecuteConfirmationModalOpen(false);
      setSearchToExecute(null);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteConfirm = () => {
    try {
      if (searchToExecute) {
        onRequestClose(searchToExecute, true, true, true);
        setIsExecuteConfirmationModalOpen(false);
        setSearchToExecute(null);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteSearchWithConfirmation = (search: ExtendedSearchesModel) => {
    try {
      setSearchToExecute(search);
      setIsExecuteConfirmationModalOpen(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleSelectSearch = (search: ExtendedSearchesModel) => {
    try {
      onViewEdit(search);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEditNameEdit = (search: ExtendedSearchesModel) => {
    try {
      if (search._id) {
        setEditSearchId(search._id);
        setEditSearchName(search.search_name || '');
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEditNameBlur = () => {
    try {
      if (editSearchId) {
        editNameSave(editSearchId, editSearchName);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEditNameCancel = () => {
    try {
      setEditSearchId(null);
      setEditSearchName('');
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEditNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setEditSearchName(e.target.value);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEditNameSave = () => {
    try {
      if (editSearchId) {
        editNameSave(editSearchId, editSearchName);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => onRequestClose(null, true, false, false)}
        maxWidth="lg"
        fullWidth
        sx={{ maxWidth: '90%', margin: 'auto' }} // Ensure the modal is centered with margin: 'auto'
        PaperProps={{
          sx: {
            maxWidth: '90%', // Ensure the content inside the modal takes the desired width
            margin: 'auto',  // Center the modal horizontally
          },
        }}
      >
        <DialogTitle>
          Saved Searches
          <IconButton onClick={() => onRequestClose(null, true, false, false)} style={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
              <CircularProgress />
              <Typography variant="body1" style={{ marginLeft: '10px' }}>Loading saved searches...</Typography>
            </div>
          ) : savedSearches.length > 0 ? (
            <TableContainer component={Paper}>
              <Table aria-label="saved searches table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sortDirection={orderBy === 'search_name' ? order : false}
                      sx={{ width: '40%', fontWeight: 'bold' }} // Set column 1 to 40%
                    >
                      <TableSortLabel
                        active={orderBy === 'search_name'}
                        direction={orderBy === 'search_name' ? order : 'asc'}
                        onClick={() => handleRequestSort('search_name')}
                      >
                        Search Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'modified_date' ? order : false}
                      sx={{ width: '15%', fontWeight: 'bold' }} // Set column 2 to 15%
                    >
                      <TableSortLabel
                        active={orderBy === 'modified_date'}
                        direction={orderBy === 'modified_date' ? order : 'asc'}
                        onClick={() => handleRequestSort('modified_date')}
                      >
                        Last Modified Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'modified_by_user' ? order : false}
                      sx={{ width: '15%', fontWeight: 'bold' }} // Set column 3 to 15%
                    >
                      <TableSortLabel
                        active={orderBy === 'modified_by_user'}
                        direction={orderBy === 'modified_by_user' ? order : 'asc'}
                        onClick={() => handleRequestSort('modified_by_user')}
                      >
                        Last Modified By
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '15%', fontWeight: 'bold', textAlign: 'center' }}> {/* Set column 4 to 30% */}
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {savedSearches.map((search) => (
                    <TableRow
                      key={search._id ?? search.search_name}
                      sx={{
                        backgroundColor: 'white', // Set a consistent background color for all rows
                        '&:hover': {
                          backgroundColor: '#e0f7fa', // Light blue highlight color on hover
                        },
                        transition: 'background-color 0.3s ease', // Smooth transition
                      }}
                    >
                      {/* Search Name Column */}
                      <TableCell sx={{ width: '40%', padding: '4px 8px' }}>
                        {editSearchId === search._id ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              value={editSearchName}
                              onChange={handleEditNameChange}
                              onBlur={handleEditNameBlur}
                              autoFocus
                              variant="outlined"
                              fullWidth
                            />
                            <IconButton onClick={handleEditNameSave} sx={{ color: 'green' }} title="Save">
                              <CheckIcon />
                            </IconButton>
                            <IconButton onClick={handleEditNameCancel} sx={{ color: 'red' }} title="Cancel">
                              <CloseIcon />
                            </IconButton>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography onClick={() => handleEditNameEdit(search)}>{search.search_name}</Typography>
                            <IconButton onClick={() => handleEditNameEdit(search)}>
                              <EditIcon />
                            </IconButton>
                          </div>
                        )}
                      </TableCell>

                      {/* Last Modified Date Column */}
                      <TableCell sx={{ width: '15%', padding: '4px 8px' }}>
                        {search.modified_date ? new Date(search.modified_date).toLocaleString() : 'N/A'}
                      </TableCell>

                      {/* Last Modified By Column */}
                      <TableCell sx={{ width: '15%', padding: '4px 8px' }}>
                        {search.modified_by_user && search.modified_by_user.first_name && search.modified_by_user.last_name
                          ? `${search.modified_by_user.first_name} ${search.modified_by_user.last_name}`
                          : 'N/A'}
                      </TableCell>

                      {/* Actions Column */}
                      <TableCell sx={{ width: '10%', padding: '4px 8px' }}>
  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}> {/* Increased gap */}
    <IconButton
      color="success"
      onClick={() => handleExecuteSearchWithConfirmation(search)}
      sx={{ fontSize: '2rem', padding: '8px' }} // Larger icon with adjusted padding
      title="Execute"
    >
      <PlayArrowIcon fontSize="inherit" />
    </IconButton>
    <IconButton
      color="primary"
      onClick={() => handleSelectSearch(search)}
      sx={{ fontSize: '2rem', padding: '8px' }} // Larger icon with adjusted padding
      title="View / Edit"
    >
      <EditIcon fontSize="inherit" />
    </IconButton>
    <IconButton
      color="error"
      onClick={() => handleDeleteSearchWithConfirmation(search)}
      sx={{ fontSize: '2rem', padding: '8px' }} // Larger icon with adjusted padding
      title="Delete"
    >
      <DeleteIcon fontSize="inherit" />
    </IconButton>
  </div>
</TableCell>


                    </TableRow>
                  ))}
                </TableBody>


              </Table>
            </TableContainer>
          ) : (
            <Typography>No saved searches available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteConfirmationModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete"
        message="Are you sure you want to delete this search?"
        confirmationType="delete"
        isProcessing={isProcessingDelete}
      />

      {/* Execute Confirmation Modal */}
      <ConfirmationModal
        isOpen={isExecuteConfirmationModalOpen}
        onClose={handleExecuteCancel}
        onConfirm={handleExecuteConfirm}
        title="Execute Search"
        message="Are you sure you want to execute this search? This action might take a while."
        confirmationType="execute"
      />
    </>
  );
};

export default SavedSearchesModal;
